import ContentIndexControls from "@common/components/ContentIndexControls";
import useSortConfig from "@common/hooks/useSortConfig";
import { useStore } from "@state/store";
import { useEffect, useState } from "react";
import styled from "styled-components";
import { Cross1Icon, HeartIcon, Share1Icon } from "@radix-ui/react-icons";
import { useFavoritesStore } from "@state/favouritesStore";
import * as Switch from "@radix-ui/react-switch";
import UnitCompare from "./components/UnitCompare";
import UnitSingle from "./components/UnitSingle";
import Prompt from "./components/Prompt";
import FloorplanSection from "@common/components/ResidenceGeneric/FloorplanSection";
import ViewSection from "@common/components/ViewSection";
import SpecsSection from "@common/components/SpecsSection";
import Modal from "@common/ui/Modal";
import Share from "./components/Share";
import { FlexTable } from "@common/components/table/FlexTable";
import TextButton from "@common/ui/TextButton";
import useLightControl from "@utils/useLightsControl";

const headers = [
  "Unit",
  "price",
  "floor",
  "unitType",
  "beds",
  <HeartIcon className="fav-icon" />,
  // "aspect",
  // "status",
];

const cells = [
  "id",
  "price",
  "floor",
  "unitType",
  "beds",
  // "aspect",
  // "status",
];

export default function Favourites(props: any) {
  const { sortConfig, setSortConfig } = useSortConfig();
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const syncState = useStore((s) => s.syncState);

  const contentControls = useStore((s) => s.contentControls);
  const stateCategories = ["Floorplan", "Specs"];

  useEffect(() => {
    syncState({
      contentControls: {
        categories: stateCategories,
        index: 0,
      },
    });
    return () => {
      syncState({
        contentControls: null,
        compareMode: false,
      });
    };
  }, []);

  const { favorites, removeFromFavorites, clearFavorites } = useFavoritesStore(
    (state) => state,
  );

  const { selectedRows, compareMode } = useStore((state) => state);

  // useEffect(() => {
  //   syncState({
  //     selectedRows: [favorites[0]],
  //     selectedUnit: favorites[0],
  //   });
  // }, []);

  const handleSelectionChange = (newRows: any[]) => {
    let updatedRows = [...selectedRows];

    if (newRows?.find((row) => row.unitName === selectedRows[0]?.unitName)) {
      updatedRows[0] = selectedRows[0];
    } else {
      updatedRows[0] = null;
    }

    if (newRows?.find((row) => row.unitName === selectedRows[1]?.unitName)) {
      updatedRows[1] = selectedRows[1];
    } else {
      updatedRows[1] = null;
    }

    newRows?.forEach((newRow) => {
      if (!updatedRows.find((row) => row && row.unitName === newRow.unitName)) {
        const nullIndex = updatedRows.indexOf(null);
        if (nullIndex !== -1) {
          updatedRows[nullIndex] = newRow;
        }
      }
    });

    syncState({ selectedRows: updatedRows });
  };

  const handleClearFavorites = () => {
    const prompt = document.querySelector(".prompt");
    prompt.classList.toggle("active");
  };

  const sections = [
    { index: 0, component: FloorplanSection },
    { index: 1, component: ViewSection },
    { index: 2, component: SpecsSection },
  ];

  const activeUnits = favorites?.filter((unit: any) => unit.active);
  const unitsToLight = activeUnits?.map((unit: any) => ({
    Name: unit.unitName?.slice(1),
  })) || [];

  useLightControl(selectedRows, unitsToLight);

  return (
    <>
      <Container className="favourites-container">
        {!favorites || favorites?.length === 0
          ? (
            <div className="no-favourites">
              Your Apartment<span>awaits</span>
            </div>
          )
          : (
            <>
              <TableContainer className="favourites-table">
                <div className="title-fav">
                  <h1>Favourite</h1>
                  <h2>Units</h2>
                </div>
                <form className="switch">
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <label
                      className="Label"
                      htmlFor="compare-mode"
                      style={{ paddingRight: 15 }}
                    >
                      COMPARE
                    </label>
                    <Switch.Root
                      className="SwitchRoot"
                      id="compare-mode"
                      defaultChecked={false}
                      onCheckedChange={(v) => {
                        syncState({ compareMode: v });
                        if (v === true) {
                          syncState({ selectedRows: [null, null] });
                        } else syncState({ selectedRows: [] });
                      }}
                    >
                      <Switch.Thumb className="SwitchThumb" />
                    </Switch.Root>
                  </div>
                </form>
                <FlexTable
                  className="table"
                  uniqueKey="unitName"
                  headers={headers}
                  cells={cells}
                  data={favorites || []}
                  sortConfig={sortConfig}
                  setSortConfig={setSortConfig}
                  selection={compareMode === true ? "double" : "single"}
                  onSelectionChange={(rows) => {
                    if (compareMode === true) {
                      handleSelectionChange(rows);
                    } else {
                      syncState({ selectedRows: rows });
                    }
                  }}
                />
                <BottomControls className="table-bottom-controls">
                  <TextButton
                    color="accent"
                    disabled={favorites.length === 0}
                    className="bottom-controls-button"
                    onClick={() => setIsShareModalOpen(true)}
                    style={{
                      fontWeight: "bold",
                      borderRadius: "2rem",
                      padding: "2rem",
                    }}
                    icon={<Share1Icon />}
                    iconPosition="right"
                  >
                    Share
                  </TextButton>
                  <TextButton
                    className="bottom-controls-button"
                    onClick={() => handleClearFavorites()}
                    icon={<Cross1Icon />}
                    iconPosition="right"
                    color="accent"
                    style={{ borderRadius: "2rem", padding: "2rem " }}
                    disabled={favorites.length === 0}
                  >
                    Clear Favourites
                  </TextButton>
                  <div className="clear-favorites">
                    <Prompt
                      onConfirm={() => {
                        clearFavorites();
                        syncState({ selectedRows: [] });
                        document
                          .querySelector(".prompt")
                          .classList.toggle("active");
                      }}
                    />
                  </div>
                </BottomControls>
              </TableContainer>
              {selectedRows[0]
                ? (
                  <InfoContainer className="favourites-info">
                    {compareMode
                      ? (
                        <UnitCompare
                          sections={sections}
                          contentControls={contentControls}
                          selectedRows={selectedRows}
                          className="unit-compare-container"
                        />
                      )
                      : (
                        <UnitSingle
                          sections={sections}
                          contentControls={contentControls}
                          selectedRows={selectedRows[0]}
                          removeFromFavorites={removeFromFavorites}
                        />
                      )}
                  </InfoContainer>
                )
                : (
                  <InfoContainer className="favourites-info">
                    <Unselected>
                      <div className="no-favourites">
                        <span>Please</span> <br /> Select from the table.
                      </div>
                    </Unselected>
                  </InfoContainer>
                )}
              <Modal
                style={{ backgroundColor: "var(--primary)" }}
                isFullScreen={true}
                isOpen={isShareModalOpen}
                onClose={() => setIsShareModalOpen(false)}
              >
                <Share show={setIsShareModalOpen} config={props} />
              </Modal>
            </>
          )}
      </Container>
    </>
  );
}

const Unselected = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const Container = styled.div``;

const InfoContainer = styled.div``;

const TableContainer = styled.div``;

const BottomControls = styled.div``;
